import './index.css';
import * as ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { router } from './router';
import { QueryClient, QueryClientProvider } from 'react-query';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { TimeSpan } from '@/utility/time-span';
import { ConfigCatEnvProvider } from '@/components';
import { ConfigCatProvider, PollingMode } from 'configcat-react';
import { ApiProvider } from '@/api/ApiProvider';
import '@/assets/styles/global.scss';
import { AuthProvider } from '@/context/auth-context.tsx';
import 'react-placeholder/lib/reactPlaceholder.css';
import { SocketProvider } from '@/providers/socket-io';
import { ReactQueryDevtools } from 'react-query/devtools';
import { DEVELOPMENT_MODE } from '@/constants';
import * as Sentry from '@sentry/react';
import { Desktop, Mobile } from '@/utility/responsive.ts';
import { DevicePermissionProvider } from '@/context/device-permission-context.tsx';
import { BrowserDetectProvider } from '@/providers/browser';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { AppearanceManager } from '@/managers/appearance-manager';

// Runs before the first paint so the app never flashes the light palette on a dark start.
AppearanceManager.init();

Sentry.init({
	dsn: process.env.SENTRY_DSN,
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration({
			maskAllText: false,
			blockAllMedia: true
		})
	],
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
	tracesSampleRate: 0.2,
	environment: process.env.MODE
});

const sessionReplayTracking = sessionReplayPlugin();

amplitude.add(sessionReplayTracking);
amplitude.add(engagementPlugin());

amplitude.init(process.env.AMPLITUDE_API_KEY!, undefined, {
	defaultTracking: {
		sessions: true,
		pageViews: { trackHistoryChanges: 'all' },
		formInteractions: false,
		fileDownloads: false
	}
});

const queryClient: QueryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById('root')!).render(
	<ConfigCatProvider
		sdkKey={process.env.CONFIG_CAT_SDK_KEY!}
		pollingMode={PollingMode.LazyLoad}
		options={{ cacheTimeToLiveSeconds: 60 * 60 * 12 }}>
		<ConfigCatEnvProvider>
			<QueryClientProvider client={queryClient}>
				{DEVELOPMENT_MODE && <ReactQueryDevtools initialIsOpen={false} />}
				<ApiProvider>
					<AuthProvider>
						<BrowserDetectProvider>
							<SocketProvider>
								<DevicePermissionProvider>
									{/* @Adem, future v7_startTransition is used to resolve console warning */}
									<RouterProvider router={router} />

									<Desktop>
										<ToastContainer
											position="bottom-right"
											autoClose={process.env.MODE === 'prod' ? TimeSpan.seconds.two : TimeSpan.seconds.five}
											hideProgressBar={true}
											newestOnTop={false}
											closeOnClick={true}
											closeButton={false}
											rtl={false}
											pauseOnFocusLoss={true}
											draggable={true}
											className={'custom-toast-container'}
											pauseOnHover={true}
											theme={'colored'}
											style={{ zIndex: 99999999 }}
										/>
									</Desktop>
									<Mobile>
										<ToastContainer
											position="top-center"
											autoClose={process.env.MODE === 'prod' ? TimeSpan.seconds.two : TimeSpan.seconds.five}
											hideProgressBar={true}
											newestOnTop={false}
											closeOnClick={true}
											closeButton={false}
											rtl={false}
											pauseOnFocusLoss={true}
											draggable={true}
											className={'custom-toast-container'}
											pauseOnHover={true}
											theme={'colored'}
											style={{ zIndex: 99999999 }}
										/>
									</Mobile>
								</DevicePermissionProvider>
							</SocketProvider>
						</BrowserDetectProvider>
					</AuthProvider>
				</ApiProvider>
			</QueryClientProvider>
		</ConfigCatEnvProvider>
	</ConfigCatProvider>
);
